import React, { useState, useRef, useEffect } from 'react';
import {
  X, Copy, CheckCircle, BarChart2, Share2, Mail, Twitter, Facebook, Link,
  Zap, Users, AlertCircle, ShoppingBag, ChevronRight, ChevronLeft,
  Target, Info, Lock, ArrowRight, Landmark, Globe,
  Download, Award, Gift, TrendingUp, MessageCircle, Instagram, QrCode,
  History, Trophy, Sparkles, Rocket, Crown, Flame, Diamond, Star, Play,
  Briefcase, UserCheck, MessageSquare, Send
} from 'lucide-react';
import QRCodeStyling from 'qr-code-styling';
import { doc, getDoc, updateDoc, arrayUnion, arrayRemove, increment } from 'firebase/firestore';
import { db } from '../../services/firebase';

interface AffiliateModalProps {
  isOpen: boolean;
  onClose: () => void;
  profile: any;
  brandOffers?: any[];
  onOpenVault: () => void;
  onOpenStore?: () => void;
  onRequestWithdrawal?: (amount: number) => void;
}

// QR Theme Options
const QR_THEMES = [
  { id: 'noir', name: 'Noir', dotColor: '#ffffff', bgColor: '#000000', ringText: 'text-zinc-500', shadow: 'shadow-[0_0_40px_rgba(255,255,255,0.1)]' },
  { id: 'volt', name: 'Volt', dotColor: '#CEFF00', bgColor: '#000000', ringText: 'text-[#CEFF00]', shadow: 'shadow-[0_0_40px_rgba(206,255,0,0.2)]' },
  { id: 'crimson', name: 'Crimson', dotColor: '#FF0000', bgColor: '#000000', ringText: 'text-[#FF0000]', shadow: 'shadow-[0_0_40px_rgba(255,0,0,0.2)]' },
  { id: 'ghost', name: 'Ghost', dotColor: '#000000', bgColor: '#ffffff', ringText: 'text-zinc-400', shadow: 'shadow-[0_0_40px_rgba(255,255,255,0.3)]' }
];

// Dynamic fake live feed generators (visual only - for motivation)
const firstNames = ['Thabo', 'Lerato', 'Sipho', 'Nomsa', 'Kagiso', 'Precious', 'Mandla', 'Zinhle', 'Lungile', 'Themba', 'Busisiwe', 'Andile', 'Naledi', 'Mpho', 'Tshiamo'];
const lastNames = ['RSA', 'JHB', 'CPT', 'DBN', 'PE', 'BFN', 'PTA', 'NW', 'MP', 'LIM'];
const actionsList = [
  { action: 'withdrew cash', amount: 'R1,250', type: 'withdrawal' },
  { action: 'withdrew cash', amount: 'R890', type: 'withdrawal' },
  { action: 'withdrew cash', amount: 'R2,100', type: 'withdrawal' },
  { action: 'earned cash', amount: 'R320', type: 'earning' },
  { action: 'earned cash', amount: 'R450', type: 'earning' },
  { action: 'earned cash', amount: 'R780', type: 'earning' },
  { action: 'got commission', amount: 'R240', type: 'earning' },
  { action: 'unlocked', amount: 'Silver Badge', type: 'unlock' },
  { action: 'unlocked', amount: 'Gold Tier', type: 'unlock' },
  { action: 'referred', amount: 'New Brand', type: 'referral' },
  { action: 'referred', amount: 'New Friend', type: 'referral' },
];

const generateRandomFeed = () => {
  const randomName = `${firstNames[Math.floor(Math.random() * firstNames.length)]}_${lastNames[Math.floor(Math.random() * lastNames.length)]}`;
  const randomAction = actionsList[Math.floor(Math.random() * actionsList.length)];
  const times = ['just now', '1 min ago', '2 mins ago', '3 mins ago', '5 mins ago', '7 mins ago', '10 mins ago', '15 mins ago'];
  return {
    id: Date.now() + Math.random(),
    name: randomName,
    action: randomAction.action,
    amount: randomAction.amount,
    timestamp: times[Math.floor(Math.random() * times.length)],
    type: randomAction.type
  };
};

export const AffiliateModal: React.FC<AffiliateModalProps> = ({
  isOpen,
  onClose,
  profile,
  brandOffers = [],
  onOpenVault,
  onOpenStore,
  onRequestWithdrawal
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'drops'>('overview');
  const [copiedTarget, setCopiedTarget] = useState<string | null>(null);
  const [showSharePopup, setShowSharePopup] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState<any>(null);
  const [showWithdrawError, setShowWithdrawError] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [showSocialModal, setShowSocialModal] = useState(false);
  const [activeTheme, setActiveTheme] = useState(QR_THEMES[0]);
  
  // Live feeds for motivation
  const [liveFeeds, setLiveFeeds] = useState<any[]>([]);
  
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const marqueeRef = useRef<HTMLDivElement>(null);
  const qrRef = useRef<HTMLDivElement>(null);
  const [qrCodeInstance, setQrCodeInstance] = useState<any>(null);

  // LOGIC CONSTANTS
  const WITHDRAWAL_THRESHOLD = 400;
  const SOCIAL_POST_EARNING = 10; 
  const BRAND_GOAL = 20;
  const FRIENDS_GOAL = 50;

  // REAL DATA FROM FIRESTORE PROFILE
  const currentBalance = profile?.balance || 0;  // REAL ZAR balance
  const brandCount = profile?.verifiedBrands || 0;      // REAL verified brand referrals
  const friendsCount = profile?.verifiedFriends || 0;   // REAL verified friend referrals
  const pendingBrands = profile?.pendingBrandInvites || 0;
  const pendingFriends = profile?.pendingFriendInvites || 0;
  const withdrawnAmount = profile?.withdrawnAmount || 0;
  const pendingAmount = profile?.pendingWithdrawal || 0;
  const socialPostCount = profile?.socialPostsApproved || 0;
  
  const lastWinner = "Sarah_JHB"; // Static for motivation
  const referralCode = profile?.handle || profile?.username || profile?.displayName?.replace(/\s/g, '').toUpperCase() || 'SCOOP24';
  const generalLink = `https://scoophype.co.za/k/${referralCode.toLowerCase()}`;

  // PROGRESS CALCULATIONS
  const withdrawProgress = Math.min((currentBalance / WITHDRAWAL_THRESHOLD) * 100, 100);
  const brandProgress = Math.min((brandCount / BRAND_GOAL) * 100, 100);
  const friendsProgress = Math.min((friendsCount / FRIENDS_GOAL) * 100, 100);
  const canWithdraw = currentBalance >= WITHDRAWAL_THRESHOLD;
  const totalInvited = brandCount + friendsCount;

  // DYNAMIC LIVE FEED
  useEffect(() => {
    const initialFeeds = Array(8).fill(null).map(() => generateRandomFeed());
    setLiveFeeds(initialFeeds);
    
    const feedInterval = setInterval(() => {
      setLiveFeeds(prev => {
        const newFeed = generateRandomFeed();
        return [newFeed, ...prev.slice(0, 14)];
      });
    }, 5000);
    
    return () => clearInterval(feedInterval);
  }, []);

  // Marquee animation for mobile
  useEffect(() => {
    if (marqueeRef.current && liveFeeds.length > 0) {
      let scrollPos = 0;
      const marqueeInterval = setInterval(() => {
        if (marqueeRef.current) {
          scrollPos -= 1;
          if (Math.abs(scrollPos) >= marqueeRef.current.scrollWidth / 2) {
            scrollPos = 0;
          }
          marqueeRef.current.scrollLeft = scrollPos;
        }
      }, 30);
      return () => clearInterval(marqueeInterval);
    }
  }, [liveFeeds]);

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [activeTab]);

  // Get S Logo for QR
  const getSLogo = (theme: typeof QR_THEMES[0]) => {
    return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='${encodeURIComponent(theme.dotColor)}' rx='25'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='central' text-anchor='middle' font-family='Arial, sans-serif' font-size='75' font-weight='900' font-style='italic' fill='${encodeURIComponent(theme.bgColor)}'%3ES%3C/text%3E%3C/svg%3E`;
  };

  // Generate QR Code
  useEffect(() => {
    if (showQR && qrRef.current) {
      const qrCode = new QRCodeStyling({
        width: 230,
        height: 230,
        data: generalLink,
        image: getSLogo(activeTheme),
        dotsOptions: { type: "rounded", color: activeTheme.dotColor },
        cornersSquareOptions: { type: "extra-rounded", color: activeTheme.dotColor },
        cornersDotOptions: { type: "dot", color: activeTheme.dotColor },
        backgroundOptions: { color: "transparent" },
        imageOptions: { crossOrigin: "anonymous", margin: 8, imageSize: 0.35 }
      });

      qrRef.current.innerHTML = '';
      qrCode.append(qrRef.current);
      setQrCodeInstance(qrCode);
    }
  }, [showQR, activeTheme, generalLink]);

  const copyToClipboard = (text: string, target: string) => {
    navigator.clipboard.writeText(text);
    setCopiedTarget(target);
    setTimeout(() => setCopiedTarget(null), 2000);
  };

  const handleShare = (platform: string, linkToShare?: string) => {
    const shareLink = linkToShare || generalLink;
    const text = `Join ScoopHype & earn rewards! Use my code: ${referralCode}`;
    const message = encodeURIComponent(text);
    const url = encodeURIComponent(shareLink);
    
    let shareUrl = '';
    switch(platform) {
      case 'whatsapp': shareUrl = `https://wa.me/?text=${message}%20${url}`; break;
      case 'twitter': shareUrl = `https://twitter.com/intent/tweet?text=${message}&url=${url}`; break;
      case 'facebook': shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`; break;
      case 'email': shareUrl = `mailto:?subject=${encodeURIComponent("Join my Scoop Network")}&body=${message}%20${url}`; break;
      case 'tiktok':
        navigator.clipboard.writeText(`${text} ${shareLink}`);
        setCopiedTarget('tiktok');
        setTimeout(() => setCopiedTarget(null), 2000);
        return;
      default: return;
    }
    if (shareUrl) window.open(shareUrl, '_blank');
  };

  const handleDownloadQR = () => {
    if (qrCodeInstance) {
      qrCodeInstance.download({ name: `scoop-${referralCode.toLowerCase()}`, extension: "png" });
    }
  };

  const openShareOptions = (specificLink?: string) => {
    setSelectedOffer(specificLink ? { link: specificLink, isSpecial: true } : null);
    setShowSharePopup(true);
  };

  // REAL WITHDRAWAL
  const handleWithdrawClick = () => {
    if (!canWithdraw) {
      setShowWithdrawError(true);
      setTimeout(() => setShowWithdrawError(false), 3000);
    } else {
      onClose();
      if (onRequestWithdrawal) {
        onRequestWithdrawal(WITHDRAWAL_THRESHOLD);
      } else if (onOpenVault) {
        onOpenVault();
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[400] bg-black/95 backdrop-blur-md flex items-center justify-center p-0 lg:p-8 animate-in fade-in duration-300">
      
      {/* QR CODE MODAL */}
      {showQR && (
        <div className="fixed inset-0 z-[600] bg-black/95 backdrop-blur-md flex items-center justify-center p-4 animate-in zoom-in duration-200">
          <div className="bg-black border border-zinc-800 w-full max-w-md overflow-hidden shadow-2xl lg:rounded-none rounded-[2rem]">
            <div className="p-5 border-b border-zinc-900 flex justify-between items-center bg-black">
              <button onClick={() => setShowQR(false)} className="w-10 h-10 flex items-center justify-center bg-zinc-900 rounded-full text-white active:scale-90 transition-transform">
                <ChevronLeft size={20} strokeWidth={3} />
              </button>
              <h2 className="text-xl font-black text-white uppercase tracking-[0.2em]">QR Code</h2>
              <button onClick={handleDownloadQR} className="w-10 h-10 flex items-center justify-center bg-white rounded-full text-black active:scale-90 transition-transform">
                <Download size={18} strokeWidth={3} />
              </button>
            </div>
            <div className="flex-1 flex flex-col p-6 items-center justify-center">
              <div className="relative w-[300px] h-[300px] flex items-center justify-center mb-8">
                <svg className="absolute inset-0 w-full h-full animate-[spin_15s_linear_infinite] pointer-events-none" viewBox="0 0 100 100">
                  <path id="circlePath" d="M 50, 50 m -45, 0 a 45,45 0 1,1 90,0 a 45,45 0 1,1 -90,0" fill="transparent" />
                  <text className={`text-[6.5px] font-black uppercase tracking-[0.2em] fill-current ${activeTheme.ringText}`}>
                    <textPath href="#circlePath" startOffset="0%">SCOOP NETWORK • VERIFIED IDENTITY • SECURE PROTOCOL • </textPath>
                  </text>
                </svg>
                <div className={`w-[250px] h-[250px] rounded-full flex items-center justify-center border-[3px] border-zinc-800 ${activeTheme.shadow} transition-all duration-500`} style={{ backgroundColor: activeTheme.bgColor }}>
                  <div ref={qrRef} className="flex items-center justify-center pointer-events-none" />
                </div>
              </div>
              <div className="text-center mb-6">
                <p className="text-zinc-500 text-[10px] font-mono mb-2">SCAN TO JOIN</p>
                <p className="text-white font-black text-xl uppercase tracking-tighter">{referralCode}</p>
              </div>
              <div className="flex gap-4">
                {QR_THEMES.map(t => (
                  <button key={t.id} onClick={() => setActiveTheme(t)} className={`w-12 h-12 rounded-full border-2 transition-all flex items-center justify-center ${activeTheme.id === t.id ? 'border-white scale-110' : 'border-zinc-800 opacity-50 hover:opacity-100'}`} style={{ backgroundColor: t.bgColor }}>
                    <div className="w-4 h-4 rounded-full" style={{ backgroundColor: t.dotColor }} />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MAIN CONTAINER */}
      <div className="w-full h-full lg:h-[90vh] max-w-7xl bg-[#050505] border-y lg:border border-zinc-800 flex flex-col lg:flex-row relative shadow-2xl overflow-hidden lg:rounded-none rounded-t-[2.5rem]">
        
        {/* MOBILE TOP NAV */}
        <div className="lg:hidden flex items-center justify-between p-5 border-b border-zinc-900 bg-black shrink-0">
          <h2 className="text-white font-black italic uppercase tracking-tighter text-blue-500">Refer & Earn Cash</h2>
          <button onClick={onClose} className="p-2 bg-zinc-900 rounded-full text-white border border-zinc-800"><X size={20}/></button>
        </div>

        {/* MOBILE: LIVE ACTIVITY MARQUEE */}
        {liveFeeds.length > 0 && (
          <div className="lg:hidden bg-gradient-to-r from-blue-950/30 to-purple-950/30 border-y border-blue-800/30 py-2 overflow-hidden">
            <div ref={marqueeRef} className="flex gap-4 overflow-x-auto scrollbar-hide whitespace-nowrap" style={{ scrollBehavior: 'smooth' }}>
              {[...liveFeeds, ...liveFeeds, ...liveFeeds].map((feed: any, idx: number) => (
                <div key={idx} className="inline-flex items-center gap-2 text-[9px] font-mono bg-black/60 px-3 py-1.5 rounded-full shrink-0 border border-blue-500/20">
                  <Flame size={10} className="text-orange-500 animate-pulse" />
                  <span className="text-white font-bold">{feed.name}</span>
                  <span className="text-zinc-400">{feed.action}</span>
                  <span className={feed.type === 'withdrawal' ? 'text-green-400 font-bold' : feed.type === 'unlock' ? 'text-purple-400 font-bold' : 'text-blue-400'}>{feed.amount}</span>
                  <span className="text-zinc-600 text-[7px]">{feed.timestamp}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* SIDEBAR (Desktop) */}
        <div className="hidden lg:flex w-[280px] bg-black border-r border-zinc-800 flex-col py-6 shrink-0 overflow-y-auto scrollbar-hide">
          <div className="px-6 mb-8">
            <h2 className="text-2xl font-black text-white italic uppercase tracking-tighter">Refer & <span className="text-blue-500">Earn</span></h2>
            <div className="flex items-center gap-3 mt-5 text-zinc-400 text-xs font-bold bg-zinc-950 p-3 border border-zinc-800">
              <div className="w-8 h-8 bg-zinc-800 flex items-center justify-center text-white border border-zinc-700">
                {profile?.displayName?.charAt(0) || 'U'}
              </div>
              <span className="truncate uppercase font-mono text-[10px]">{profile?.displayName || 'USER'}</span>
            </div>
          </div>

          <nav className="flex-1 space-y-0.5 px-4">
            <button onClick={() => setActiveTab('overview')} className={`w-full flex items-center gap-3 px-4 py-3.5 transition-colors border-l-2 ${activeTab === 'overview' ? 'bg-zinc-950 border-white text-white' : 'border-transparent text-zinc-600 hover:text-white hover:bg-zinc-950/50'}`}>
              <BarChart2 size={16} className={activeTab === 'overview' ? 'text-blue-500' : ''}/> 
              <div className="text-left"><p className="font-bold text-[11px] uppercase tracking-wider">Mission Control</p><p className="text-[8px] uppercase font-mono text-zinc-600 mt-0.5">Stats & Cash</p></div>
            </button>
            <button onClick={() => setActiveTab('drops')} className={`w-full flex items-center gap-3 px-4 py-3.5 transition-colors border-l-2 ${activeTab === 'drops' ? 'bg-zinc-950 border-white text-white' : 'border-transparent text-zinc-600 hover:text-white hover:bg-zinc-950/50'}`}>
              <ShoppingBag size={16} className={activeTab === 'drops' ? 'text-blue-500' : ''}/> 
              <div className="text-left"><p className="font-bold text-[11px] uppercase tracking-wider">Brand Drops</p><p className="text-[8px] uppercase font-mono text-blue-500 mt-0.5">Live Cash Offers</p></div>
            </button>
          </nav>

          {/* DESKTOP: LIVE ACTIVITY FEED */}
          {liveFeeds.length > 0 && (
            <div className="mt-8 px-4 pt-4 border-t border-zinc-900">
              <div className="flex items-center gap-2 mb-3"><Flame size={12} className="text-orange-500 animate-pulse" /><p className="text-[8px] font-black uppercase tracking-widest text-zinc-500">Live Activity</p><span className="text-[6px] text-green-400 ml-auto animate-pulse">● LIVE</span></div>
              <div className="space-y-2 max-h-[250px] overflow-y-auto">
                {liveFeeds.slice(0, 12).map((feed: any, idx: number) => (
                  <div key={feed.id || idx} className="text-[9px] font-mono bg-zinc-950 p-2 border-l-2 border-blue-500/50">
                    <div className="flex justify-between items-start">
                      <div><span className="text-white font-bold">{feed.name}</span><span className="text-zinc-500"> {feed.action} </span><span className={feed.type === 'withdrawal' ? 'text-green-400 font-bold' : feed.type === 'unlock' ? 'text-purple-400 font-bold' : 'text-blue-400'}>{feed.amount}</span></div>
                      <span className="text-zinc-600 text-[6px]">{feed.timestamp}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* MOBILE BOTTOM TAB NAV */}
        <div className="lg:hidden flex border-b border-zinc-900 bg-black shrink-0">
          <button onClick={() => setActiveTab('overview')} className={`flex-1 py-3 text-[9px] font-black uppercase tracking-widest transition-colors relative flex items-center justify-center gap-1 ${activeTab === 'overview' ? 'text-white' : 'text-zinc-600'}`}><BarChart2 size={14} /> Mission {activeTab === 'overview' && <div className="absolute bottom-0 left-0 w-full h-[2px] bg-white" />}</button>
          <button onClick={() => setActiveTab('drops')} className={`flex-1 py-3 text-[9px] font-black uppercase tracking-widest transition-colors relative flex items-center justify-center gap-1 ${activeTab === 'drops' ? 'text-white' : 'text-zinc-600'}`}><ShoppingBag size={14} /> Drops {activeTab === 'drops' && <div className="absolute bottom-0 left-0 w-full h-[2px] bg-white" />}</button>
        </div>

        {/* MAIN CONTENT AREA */}
        <div className="flex-1 flex flex-col min-h-0 bg-[#050505] relative">
          <button onClick={onClose} className="hidden lg:flex absolute top-6 right-6 p-2 bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-white transition-colors z-50 rounded-none"><X size={18}/></button>

          <div ref={scrollContainerRef} className="flex-1 overflow-y-auto scrollbar-hide p-4 lg:p-10 space-y-6 pb-24 lg:pb-10">
            
            {/* MAIN OVERVIEW TAB */}
            {activeTab === 'overview' && (
              <div className="space-y-6 animate-in slide-in-from-right-4 duration-300">

                {/* AVAILABLE BALANCE (Cash Focus) */}
                <div className="bg-zinc-950 border border-zinc-900 p-6 lg:p-8 rounded-3xl lg:rounded-none">
                  <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
                    <div>
                      <p className="text-zinc-500 text-[10px] uppercase tracking-wider font-black mb-2 flex items-center gap-1.5">
                        <Landmark size={14} className="text-green-500"/> Total Cash Earned
                      </p>
                      <p className="text-4xl lg:text-5xl font-black text-white leading-none">
                        R{currentBalance.toLocaleString()} <span className="text-xl text-green-500 font-mono italic">ZAR</span>
                      </p>
                    </div>
                    
                    <button 
                      onClick={handleWithdrawClick}
                      className={`w-full lg:w-auto px-10 py-3.5 font-black uppercase tracking-widest text-[10px] transition-all rounded-full lg:rounded-none flex items-center justify-center ${canWithdraw ? 'bg-green-600 text-white hover:bg-green-500' : 'bg-zinc-900 border border-zinc-800 text-zinc-600 cursor-not-allowed'}`}
                    >
                      {canWithdraw ? `Withdraw R${WITHDRAWAL_THRESHOLD}` : 'Access Vault To Withdraw R400'}
                    </button>
                  </div>

                  <div className="mt-6">
                    <div className="flex justify-between text-[8px] font-mono uppercase tracking-widest mb-2">
                      <span className="text-zinc-500">Progress to R{WITHDRAWAL_THRESHOLD} Withdrawal</span>
                      <span className="text-white">{Math.floor(withdrawProgress)}%</span>
                    </div>
                    <div className="h-3 bg-zinc-800 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-green-700 via-green-500 to-emerald-400 rounded-full transition-all duration-1000 relative"
                        style={{ width: `${withdrawProgress}%` }}
                      >
                        {withdrawProgress > 5 && <div className="absolute right-0 top-1/2 -translate-y-1/2 w-2 h-2 bg-white rounded-full animate-pulse" />}
                      </div>
                    </div>
                    <p className="text-[8px] text-zinc-600 mt-2 text-center font-mono uppercase">
                      {currentBalance < WITHDRAWAL_THRESHOLD ? `Need R${WITHDRAWAL_THRESHOLD - currentBalance} more cash to withdraw` : 'Goal reached! Ready to withdraw your cash.'}
                    </p>
                  </div>

                  {showWithdrawError && !canWithdraw && (
                    <div className="mt-5 p-4 bg-amber-950/20 border border-amber-900/50 rounded-xl lg:rounded-none flex items-center gap-3">
                      <AlertCircle size={16} className="text-amber-500 shrink-0"/>
                      <p className="text-amber-400 text-[9px] font-mono uppercase tracking-widest">Minimum withdrawal: R{WITHDRAWAL_THRESHOLD} ZAR</p>
                    </div>
                  )}
                </div>

                {/* FRIENDS & BRANDS SECTIONS WITH REAL DATA */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Friends Section */}
                  <div className="bg-zinc-950 border border-zinc-900 p-6 rounded-3xl lg:rounded-none">
                    <div className="flex justify-between items-center mb-3">
                      <div className="flex items-center gap-2"><Users size={16} className="text-blue-500" /><h3 className="text-white font-black text-sm uppercase tracking-wider">Refer Friends</h3></div>
                      <button onClick={() => openShareOptions(generalLink)} className="px-4 py-2 bg-blue-600 text-white font-black text-[9px] uppercase tracking-widest rounded-full hover:bg-blue-500 transition-colors flex items-center gap-1.5"><Share2 size={12} /> Share</button>
                    </div>
                    <div className="flex justify-between items-center mb-2"><span className="text-zinc-400 text-[10px]">Verified Progress</span><span className="text-blue-400 font-mono text-[10px] font-bold">{friendsCount}/{FRIENDS_GOAL}</span></div>
                    <div className="h-2 bg-zinc-800 overflow-hidden rounded-full mb-2"><div className="h-full bg-blue-500 transition-all duration-1000" style={{ width: `${friendsProgress}%` }} /></div>
                    <p className="text-[8px] text-green-400 mt-2 font-bold">🎁 R200 Cash Bonus at {FRIENDS_GOAL} friends</p>
                    {pendingFriends > 0 && <p className="text-[7px] text-amber-500 mt-1">⏳ {pendingFriends} pending verification</p>}
                  </div>

                  {/* Brands Section */}
                  <div className="bg-zinc-950 border border-zinc-900 p-6 rounded-3xl lg:rounded-none">
                    <div className="flex justify-between items-center mb-3">
                      <div className="flex items-center gap-2"><Briefcase size={16} className="text-purple-500" /><h3 className="text-white font-black text-sm uppercase tracking-wider">Refer Brands</h3></div>
                      <button onClick={() => openShareOptions(generalLink)} className="px-4 py-2 bg-purple-600 text-white font-black text-[9px] uppercase tracking-widest rounded-full hover:bg-purple-500 transition-colors flex items-center gap-1.5"><Share2 size={12} /> Share</button>
                    </div>
                    <div className="flex justify-between items-center mb-2"><span className="text-zinc-400 text-[10px]">Verified Progress</span><span className="text-purple-400 font-mono text-[10px] font-bold">{brandCount}/{BRAND_GOAL}</span></div>
                    <div className="h-2 bg-zinc-800 overflow-hidden rounded-full mb-2"><div className="h-full bg-purple-500 transition-all duration-1000" style={{ width: `${brandProgress}%` }} /></div>
                    <p className="text-[8px] text-green-400 mt-2 font-bold">🎁 R200 Cash Bonus at {BRAND_GOAL} brands</p>
                    {pendingBrands > 0 && <p className="text-[7px] text-amber-500 mt-1">⏳ {pendingBrands} pending verification</p>}
                  </div>
                </div>

                {/* EARNING BLUEPRINT */}
                <div className="bg-gradient-to-r from-blue-950/30 to-purple-950/30 border border-blue-800/30 overflow-hidden rounded-3xl lg:rounded-none w-full">
                  <div className="p-5 bg-black/50 border-b border-blue-800/30"><h3 className="text-white font-black text-sm uppercase tracking-widest flex items-center gap-2"><Zap size={16} className="text-yellow-500" /> Cash Blueprint</h3></div>
                  <div className="p-6"><div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="flex gap-4 items-start"><div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center shrink-0"><span className="text-blue-400 font-black text-sm">01</span></div><div><p className="text-white font-black text-[11px] uppercase">Invite Friends/Brands</p><p className="text-zinc-400 text-[9px] mt-1">Share your unique referral link or code</p></div></div>
                    <div className="flex gap-4 items-start"><div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center shrink-0"><span className="text-purple-400 font-black text-sm">02</span></div><div><p className="text-white font-black text-[11px] uppercase">They Sign Up & Verify</p><p className="text-zinc-400 text-[9px] mt-1">Earn real cash when they join</p></div></div>
                    <div className="flex gap-4 items-start"><div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center shrink-0"><span className="text-green-400 font-black text-sm">03</span></div><div><p className="text-white font-black text-[11px] uppercase">Get Paid Instantly</p><p className="text-zinc-400 text-[9px] mt-1">R4 per friend • R10 per brand</p></div></div>
                  </div></div>
                </div>

                {/* SOCIAL MEDIA EARNING SECTION */}
                <div className="bg-gradient-to-r from-pink-950/20 to-purple-950/20 border border-pink-800/30 rounded-3xl lg:rounded-none p-6">
                  <div className="flex flex-col gap-4">
                    <div className="flex items-start justify-between"><div><p className="text-white font-black text-sm uppercase tracking-wider">Post on Social Media = R10 Cash!</p><p className="text-zinc-400 text-[10px] font-mono mt-1">Select a platform to share your link and earn</p></div></div>
                    <div className="flex gap-3 mt-2 flex-wrap">
                      <button onClick={() => setShowSocialModal(true)} className="w-12 h-12 rounded-xl bg-pink-950/50 border border-pink-900/50 flex items-center justify-center hover:bg-pink-900 transition-colors group"><Instagram size={18} className="text-pink-400 group-hover:scale-110 transition-transform" /></button>
                      <button onClick={() => setShowSocialModal(true)} className="w-12 h-12 rounded-xl bg-blue-950/50 border border-blue-900/50 flex items-center justify-center hover:bg-blue-900 transition-colors group"><Twitter size={18} className="text-blue-400 group-hover:scale-110 transition-transform" /></button>
                      <button onClick={() => setShowSocialModal(true)} className="w-12 h-12 rounded-xl bg-green-950/50 border border-green-900/50 flex items-center justify-center hover:bg-green-900 transition-colors group"><MessageCircle size={18} className="text-green-400 group-hover:scale-110 transition-transform" /></button>
                      <button onClick={() => setShowSocialModal(true)} className="w-12 h-12 rounded-xl bg-cyan-950/50 border border-cyan-900/50 flex items-center justify-center hover:bg-cyan-900 transition-colors group"><Send size={18} className="text-cyan-400 group-hover:scale-110 transition-transform" /></button>
                    </div>
                  </div>
                  {socialPostCount > 0 && (<div className="mt-4 pt-3 border-t border-pink-800/30 flex justify-between text-[9px]"><span className="text-zinc-500">Posts approved:</span><span className="text-pink-400 font-bold">{socialPostCount} × R{SOCIAL_POST_EARNING} = R{socialPostCount * SOCIAL_POST_EARNING}</span></div>)}
                </div>

                {/* SHARE LINKS SECTION */}
                <div className="bg-zinc-950 border border-zinc-900 overflow-hidden rounded-3xl lg:rounded-none">
                  <div className="p-6 space-y-5">
                    <div><p className="text-white text-[10px] font-black mb-2 uppercase tracking-widest">Your Invite Link</p><div className="bg-black border border-zinc-800 p-3 flex items-center justify-between rounded-xl lg:rounded-none"><span className="text-zinc-400 text-[10px] font-mono truncate mr-4">{generalLink}</span><button onClick={() => copyToClipboard(generalLink, 'link')} className="p-2 bg-zinc-900 text-zinc-400 hover:text-white transition-colors border border-zinc-800 shrink-0">{copiedTarget === 'link' ? <CheckCircle size={14} className="text-green-500"/> : <Copy size={14}/>}</button></div></div>
                    <div><p className="text-white text-[10px] font-black mb-2 uppercase tracking-widest">Your Referral Code</p><div className="bg-black border border-zinc-800 p-3 flex items-center justify-between rounded-xl lg:rounded-none"><span className="text-blue-500 font-black text-lg font-mono">{referralCode}</span><button onClick={() => copyToClipboard(referralCode, 'code')} className="p-2 bg-zinc-900 text-zinc-400 hover:text-white transition-colors border border-zinc-800 shrink-0">{copiedTarget === 'code' ? <CheckCircle size={14} className="text-green-500"/> : <Copy size={14}/>}</button></div></div>
                  </div>
                  <div className="p-6 flex flex-wrap gap-4 justify-between items-center bg-blue-950/10 border-t border-zinc-900">
                    <div className="flex gap-3"><button onClick={() => openShareOptions(generalLink)} className="px-6 py-2.5 bg-white text-black font-black text-[10px] uppercase tracking-widest rounded-full lg:rounded-none hover:bg-zinc-200 transition-colors">Broadcast Link</button><button onClick={() => setShowQR(true)} className="px-5 py-2.5 border border-white text-white font-black text-[10px] uppercase tracking-widest rounded-full lg:rounded-none hover:bg-white/10 transition-colors flex items-center gap-1.5"><QrCode size={12}/> QR</button></div>
                    <p className="text-[8px] text-zinc-500 uppercase font-mono tracking-widest">🎁 New users get R2,000 Welcome Bundle</p>
                  </div>
                </div>

                {/* REFERRAL RACE PROMO (Static for motivation) */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="bg-gradient-to-r from-red-950/20 to-black border border-red-800/30 p-6 rounded-3xl lg:rounded-none">
                    <div className="flex justify-between items-start">
                      <div><h3 className="text-white font-black text-[11px] uppercase tracking-wider flex items-center gap-2"><Trophy size={14} className="text-red-400"/> R20,000 Referral Race</h3><p className="text-[8px] text-zinc-500 font-mono uppercase tracking-widest">Ends 17 May 2026, 16:00</p></div>
                      <button className="px-4 py-2 bg-white text-black text-[8px] font-black uppercase rounded-full lg:rounded-none">Join Race</button>
                    </div>
                    <div className="mt-5 flex justify-between items-center bg-black/40 p-4 border border-red-900/30 rounded-xl lg:rounded-none">
                      <div><p className="text-[8px] text-zinc-500 uppercase font-black mb-1">Your Score</p><p className="text-white font-black text-sm">{totalInvited}</p></div>
                      <div><p className="text-[8px] text-zinc-500 uppercase font-black mb-1">To Next Prize</p><p className="text-amber-400 font-black text-sm">{totalInvited + 1}</p></div>
                      <div><p className="text-[8px] text-amber-400 font-black uppercase">Refer 1 → +R20 Cash</p></div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-amber-950/20 to-black border border-amber-800/30 p-6 rounded-3xl lg:rounded-none">
                    <div className="flex justify-between items-center">
                      <div><h3 className="text-white font-black text-[11px] uppercase tracking-wider flex items-center gap-2"><Crown size={14} className="text-amber-400"/> Top Rank Cash Bonus</h3><p className="text-2xl font-black text-amber-400 mt-2">R50,000</p><p className="text-[8px] text-yellow-400 mt-1">🏆 Last Won by {lastWinner}</p></div>
                      <button onClick={() => openShareOptions(generalLink)} className="px-5 py-2 bg-amber-600 text-white text-[9px] font-black uppercase tracking-widest rounded-full lg:rounded-none hover:bg-amber-500 transition-colors">Join</button>
                    </div>
                    <p className="text-zinc-400 text-[8px] uppercase font-mono tracking-widest mt-3">Be the top referrer this month!</p>
                  </div>
                </div>

              </div>
            )}

            {/* TAB 2: BRAND DROPS */}
            {activeTab === 'drops' && (
              <div className="space-y-6 animate-in slide-in-from-right-4 duration-300">
                <div className="mb-8"><h2 className="text-2xl font-black text-white uppercase italic tracking-tighter">Live Brand Drops</h2><p className="text-zinc-500 text-[10px] font-mono mt-2 uppercase tracking-widest">Share unique links for active bounties & earn cash commission.</p></div>
                {brandOffers && brandOffers.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {brandOffers.map((offer: any) => (
                      <div key={offer.id} className="bg-zinc-950 border border-zinc-900 flex flex-col rounded-3xl lg:rounded-none overflow-hidden group">
                        <div className="h-48 bg-black relative overflow-hidden border-b border-zinc-900"><img src={offer.image || offer.imageUrl} alt={offer.name} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700" /><div className="absolute top-3 left-3"><span className="text-[8px] px-2 py-1 bg-black/90 border border-zinc-800 text-blue-500 font-black uppercase tracking-widest">{offer.offerType || 'Active'}</span></div><div className="absolute bottom-3 right-3 bg-green-600 text-white text-[8px] px-2 py-0.5 font-black rounded-full">+{offer.commissionRate || '20'}% Cash</div></div>
                        <div className="p-5 flex-1 flex flex-col"><h3 className="text-white font-black text-sm uppercase tracking-tight truncate">{offer.name || 'Brand Offer'}</h3><p className="text-zinc-500 text-[9px] font-mono mt-1">Partner: {offer.brandName || 'Premium Brand'}</p><button onClick={() => openShareOptions(offer.link || generalLink)} className="mt-4 w-full py-2.5 bg-white text-black font-black text-[9px] uppercase tracking-widest rounded-full lg:rounded-none hover:bg-zinc-200 transition-colors flex items-center justify-center gap-2"><Share2 size={12} /> Get Share Link</button></div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-20 border border-dashed border-zinc-900 rounded-3xl lg:rounded-none bg-black/50"><AlertCircle size={48} className="mx-auto mb-4 text-zinc-800" strokeWidth={1}/><p className="text-zinc-500 text-[10px] font-black uppercase tracking-widest">No active drops</p><p className="text-zinc-600 text-[9px] font-mono uppercase mt-2">Use your invite link to build your cash network</p></div>
                )}
              </div>
            )}
            
          </div>
        </div>
      </div>

      {/* SHARE POPUP WITH ALL OPTIONS */}
      {showSharePopup && (
        <div className="fixed inset-0 z-[500] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 animate-in zoom-in duration-200">
          <div className="bg-[#050505] border border-zinc-800 w-full max-w-sm overflow-hidden shadow-2xl lg:rounded-none rounded-[2rem]">
            <div className="flex justify-between items-center p-5 border-b border-zinc-900 bg-black"><h3 className="text-base font-black text-white uppercase italic tracking-widest">Share Link</h3><button onClick={() => setShowSharePopup(false)} className="p-2 text-zinc-500 hover:text-white bg-zinc-900 border border-zinc-800 rounded-full transition-colors"><X size={16}/></button></div>
            <div className="p-6">
              <div className="bg-black border border-zinc-900 p-5 mb-6 rounded-xl relative group"><p className="text-[10px] text-zinc-400 font-mono leading-relaxed break-words pr-8">🚀 Join Scoop Hub & earn real cash! Use code: <span className="text-blue-500 font-bold">{referralCode}</span> → {generalLink}</p><button onClick={() => copyToClipboard(generalLink, 'popupCopy')} className="absolute top-4 right-4 p-2 bg-zinc-900 border border-zinc-800 text-zinc-500 hover:text-white transition-colors">{copiedTarget === 'popupCopy' ? <CheckCircle size={14} className="text-green-500"/> : <Copy size={14}/>}</button></div>
              <div className="space-y-3"><label className="text-[10px] font-black text-zinc-500 uppercase tracking-widest block">Share via</label><div className="grid grid-cols-5 gap-3">
                <button onClick={() => handleShare('whatsapp')} className="flex flex-col items-center gap-1 group"><div className="w-12 h-12 bg-zinc-900 border border-zinc-800 flex items-center justify-center group-hover:bg-green-600 transition-all rounded-full"><MessageCircle size={18} className="text-zinc-400 group-hover:text-white" /></div><span className="text-[7px] uppercase text-zinc-500 font-black tracking-widest">WhatsApp</span></button>
                <button onClick={() => handleShare('twitter')} className="flex flex-col items-center gap-1 group"><div className="w-12 h-12 bg-zinc-900 border border-zinc-800 flex items-center justify-center group-hover:bg-black transition-all rounded-full"><Twitter size={18} className="text-zinc-400 group-hover:text-white" /></div><span className="text-[7px] uppercase text-zinc-500 font-black tracking-widest">X</span></button>
                <button onClick={() => handleShare('facebook')} className="flex flex-col items-center gap-1 group"><div className="w-12 h-12 bg-zinc-900 border border-zinc-800 flex items-center justify-center group-hover:bg-blue-600 transition-all rounded-full"><Facebook size={18} className="text-zinc-400 group-hover:text-white" /></div><span className="text-[7px] uppercase text-zinc-500 font-black tracking-widest">FB</span></button>
                <button onClick={() => handleShare('email')} className="flex flex-col items-center gap-1 group"><div className="w-12 h-12 bg-zinc-900 border border-zinc-800 flex items-center justify-center group-hover:bg-blue-500 transition-all rounded-full"><Mail size={18} className="text-zinc-400 group-hover:text-white" /></div><span className="text-[7px] uppercase text-zinc-500 font-black tracking-widest">Mail</span></button>
                <button onClick={() => handleShare('tiktok')} className="flex flex-col items-center gap-1 group"><div className="w-12 h-12 bg-zinc-900 border border-zinc-800 flex items-center justify-center group-hover:bg-black transition-all rounded-full"><Send size={18} className="text-zinc-400 group-hover:text-white" /></div><span className="text-[7px] uppercase text-zinc-500 font-black tracking-widest">TikTok</span></button>
              </div></div>
              <div className="mt-6 pt-4 border-t border-zinc-800"><button onClick={() => copyToClipboard(generalLink, 'directCopy')} className="w-full py-3 bg-zinc-900 border border-zinc-800 text-white font-black text-[10px] uppercase tracking-widest rounded-full hover:bg-zinc-800 transition-colors flex items-center justify-center gap-2">{copiedTarget === 'directCopy' ? <CheckCircle size={14} className="text-green-500"/> : <Copy size={14}/>} Copy Link</button></div>
            </div>
          </div>
        </div>
      )}

      {/* SOCIAL MEDIA POST MODAL */}
      {showSocialModal && (
        <div className="fixed inset-0 z-[500] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 animate-in zoom-in duration-200">
          <div className="bg-[#050505] border border-pink-800 w-full max-w-md overflow-hidden shadow-2xl lg:rounded-none rounded-[2rem]">
            <div className="flex justify-between items-center p-5 border-b border-pink-800/30 bg-black">
              <h3 className="text-base font-black text-white uppercase italic tracking-widest flex items-center gap-2">
                <Share2 size={18} className="text-pink-400" /> Post & Earn R10
              </h3>
              <button onClick={() => setShowSocialModal(false)} className="p-2 text-zinc-500 hover:text-white bg-zinc-900 border border-zinc-800 rounded-full transition-colors">
                <X size={16}/>
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[80vh] scrollbar-hide">
              <div className="bg-black border border-pink-800/30 p-4 mb-5 rounded-xl">
                <p className="text-xs text-white font-black uppercase tracking-widest mb-3">📋 Mission Criteria:</p>
                <ul className="text-[10px] text-zinc-400 space-y-3 ml-1">
                  <li className="flex gap-2">
                    <span className="text-pink-500 font-bold">01</span>
                    <span>Post a video talking about how Scoop allows local brands to onboard and sell their products.</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-pink-500 font-bold">02</span>
                    <span>OR post a screenshot of your dashboard explaining the platform features.</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-pink-500 font-bold">03</span>
                    <span>Add your Referral Link in your bio and mention your code in the caption.</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-pink-500 font-bold">04</span>
                    <span>Submit the live link below for verification. Earn R10 cash per approved post!</span>
                  </li>
                </ul>
              </div>
              
              <div className="bg-black border border-zinc-800 p-4 rounded-xl mb-5">
                <div className="flex justify-between items-center mb-2">
                  <p className="text-[10px] text-zinc-400 uppercase font-black tracking-widest">Suggested Content:</p>
                  <button 
                    onClick={() => copyToClipboard(`#ad 🚀 Join the wave on @ScoopHype! Secure your bag by referring brands or friends. 🎁 Get a R2,000 Coupon Bundle when you join. 🔍 Search code: ${referralCode} in the Scoop app to verify. 🔗 Link in bio!`, 'captionCopy')}
                    className="text-[9px] text-blue-500 font-black uppercase hover:text-white transition-colors"
                  >
                    {copiedTarget === 'captionCopy' ? 'Copied ✓' : 'Copy Caption'}
                  </button>
                </div>
                <div className="p-3 bg-zinc-950 border border-zinc-900 rounded text-[9px] text-zinc-500 font-mono leading-relaxed italic">
                  "#ad 🚀 Join the wave on @ScoopHype! Secure your bag by referring brands or friends... 🔍 Search code: {referralCode}..."
                </div>
              </div>

              <div className="bg-black border border-zinc-800 p-4 rounded-xl mb-5">
                <p className="text-[10px] text-zinc-400 mb-2 uppercase font-black tracking-widest">Your unique link:</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-[9px] text-blue-400 font-mono break-all bg-zinc-950 p-2 rounded border border-zinc-900">{generalLink}</code>
                  <button onClick={() => copyToClipboard(generalLink, 'socialCopy')} className="p-2 bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-white transition-colors shrink-0">
                    {copiedTarget === 'socialCopy' ? <CheckCircle size={14} className="text-green-500"/> : <Copy size={14}/>}
                  </button>
                </div>
              </div>
              
              <div className="space-y-2 mb-5">
                <label className="text-[10px] text-zinc-400 uppercase font-black tracking-widest ml-1">Live Post URL:</label>
                <input
                  type="url"
                  placeholder="https://www.instagram.com/p/..."
                  className="w-full p-4 bg-black border border-zinc-800 rounded-xl text-white text-sm placeholder:text-zinc-700 focus:outline-none focus:border-pink-500 transition-colors font-mono"
                />
              </div>
              
              <button 
                onClick={() => {
                  setShowSocialModal(false);
                  alert("Post submitted for manual verification. Cash rewards are paid within 24 hours.");
                }}
                className="w-full py-4 bg-gradient-to-r from-pink-600 to-purple-600 text-white font-black text-[11px] uppercase tracking-widest rounded-xl hover:opacity-90 transition-all flex items-center justify-center gap-2 shadow-[0_0_20px_rgba(219,39,119,0.3)] active:scale-95"
              >
                <Share2 size={14} /> Submit for Review (+R{SOCIAL_POST_EARNING})
              </button>
              
              <p className="text-[8px] text-zinc-600 text-center mt-4 uppercase tracking-[0.1em] font-black">
                Account verification required for R10 cash payout.
              </p>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};